// src/pages/ServerDataExtractionPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ExtractionOptions from '../components/ServerDataExtraction/ExtractionOptions';
import ExtractionResults from '../components/ServerDataExtraction/ExtractionResults';
import ExtractionHistory from '../components/ServerDataExtraction/ExtractionHistory';
import ServerDataExtractionHeader from '../components/ServerDataExtraction/ServerDataExtractionHeader';
import DataExtractionTips from '../components/ServerDataExtraction/DataExtractionTips';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import PageTransition from '../components/Shared/PageTransition';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import TermsOfServiceModal from '../components/Shared/TermsOfServiceModal';
import {
    WS_SERVER_DATA_TYPES,
    WS_SERVER_DATA_TOPICS,
    getDataAppEndpoint,
    getDataUserQueueTopic
} from '../config/wsServerDataExtractionConfig';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import { useNotifications } from '../contexts/NotificationContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';

import './DataExtractionPage.css';

const ServerDataExtractionPage = ({ openModal }) => {
    const { language, t } = useLanguage();
    const isEn = language === 'en';

    const { isAuthenticated, user, isLoading: isAuthLoading, triggerGoogleLogin } = useAuth();
    const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
    const { serverInfo, connectionError, isConnecting, isConnectedToServer, targetIp } = useServerConnection();
    const { addNotification, showToast } = useNotifications();

    // --- State Management ---
    const [extractionOptions, setExtractionOptions] = useState({
        serverInfo: true,
        motd: true,
        serverIcon: true,
        playerList: true,
        pluginList: true,
        dnsHistory: true,
        subdomainScan: true,
        wafDetection: true,
        portScan: false,
        cveAudit: true,
        modsManifest: false,
        geoDatacenter: true,
        outputFormat: 'json'
    });
    const [extractionStatus, setExtractionStatus] = useState('idle');
    const [isExtractingLoading, setIsExtractingLoading] = useState(false);
    const [extractedData, setExtractedData] = useState(null);
    const [extractionError, setExtractionError] = useState(null);
    const [extractionProgress, setExtractionProgress] = useState(null);
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [extractionHistory, setExtractionHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [generalWsError, setGeneralWsError] = useState(null);

    const subscriptions = useRef({
        extractionStatus: null,
        extractionProgress: null,
        extractionError: null,
        history: null
    });
    const historyRequestedRef = useRef(false);
    const currentServerIpRef = useRef(null);

    // Track current server IP and reset extraction state on server change
    useEffect(() => {
        const newIp = serverInfo?.hostname || serverInfo?.ip || targetIp || null;
        if (currentServerIpRef.current && newIp && currentServerIpRef.current !== newIp) {
            setExtractedData(null);
            setExtractionStatus('idle');
            setExtractionProgress(null);
            setExtractionError(null);
        }
        currentServerIpRef.current = newIp;
    }, [serverInfo, targetIp]);

    // --- WebSocket Handlers ---
    const handleExtractionStatusUpdate = useCallback((message, wsError) => {
        if (wsError) {
            setGeneralWsError(t('dataExtractionError'));
            setIsExtractingLoading(false);
            showToast(t('dataExtractionError') || 'Extraction Error');
            return;
        }
        setGeneralWsError(null);

        switch (message?.type) {
            case WS_SERVER_DATA_TYPES.EXTRACTION_STARTED:
                setIsExtractingLoading(true);
                setExtractionStatus('extracting');
                setExtractionError(null);
                setExtractedData(null);
                setExtractionProgress({
                    percent: 25,
                    step: t('dataExtractionInProgress'),
                    message: message.message || (isEn ? 'Extracting server data...' : 'Выкачивание данных...')
                });
                break;

            case WS_SERVER_DATA_TYPES.EXTRACTION_COMPLETED:
                setIsExtractingLoading(false);
                const success = message.data?.success === true;
                const status = success ? (message.status || 'complete') : 'error';
                const newData = message.data || {};
                const errorMsg = !success
                    ? (message.data?.error || t('dataExtractionFailed'))
                    : (status === 'partial' ? (message.message || t('dataExtractionCompleted')) : null);

                setExtractionStatus(status);

                if (success) {
                    window.dispatchEvent(new CustomEvent('showAiInvite', {
                        detail: { message: t('aiAssistantInviteData') }
                    }));
                }

                setExtractedData(newData);
                setExtractionError(errorMsg);
                setExtractionProgress(null);

                if (success) {
                    showToast(isEn ? 'Data successfully extracted' : 'Данные успешно извлечены');
                    const historyEntry = {
                        server: serverInfo?.hostname || currentServerIpRef.current,
                        host: currentServerIpRef.current,
                        ip: currentServerIpRef.current,
                        time: isEn ? 'Just now' : 'Только что',
                        date: new Date().toLocaleTimeString(),
                        format: (extractionOptions.outputFormat || 'json').toUpperCase(),
                        players: newData.onlinePlayers || (newData.players?.length) || (serverInfo?.players?.online) || 0,
                        status: status,
                        data: newData
                    };
                    setExtractionHistory(prev => [historyEntry, ...prev.slice(0, 7)]);
                } else if (errorMsg) {
                    showToast(`${isEn ? 'Error: ' : 'Ошибка: '}${errorMsg}`);
                }
                break;

            case WS_SERVER_DATA_TYPES.CURRENT_STATE:
                if (message.extraction && message.serverInfo?.ip === currentServerIpRef.current) {
                    setExtractionOptions(prev => ({ ...prev, ...(message.extraction.options || {}) }));
                    setExtractionStatus(message.extraction.status || 'idle');
                    setExtractedData(message.extraction.data || null);
                    setIsExtractingLoading(message.extraction.status === 'extracting');
                    setExtractionError(message.extraction.status === 'error' ? t('dataExtractionError') : null);
                    setExtractionProgress(null);
                } else if (!message.extraction && message.serverInfo?.ip === currentServerIpRef.current) {
                    setExtractionStatus('idle');
                    setExtractedData(null);
                    setExtractionError(null);
                    setExtractionProgress(null);
                }
                break;

            default:
                break;
        }
    }, [serverInfo, extractionOptions, showToast, t, isEn]);

    const handleExtractionProgressUpdate = useCallback((message, wsError) => {
        if (wsError || !message) return;
        if (message?.type === WS_SERVER_DATA_TYPES.EXTRACTION_PROGRESS && message.ip === currentServerIpRef.current) {
            if (extractionStatus === 'extracting' || isExtractingLoading) {
                setExtractionProgress({
                    percent: message.percent || 65,
                    step: message.step || t('dataExtractionInProgress'),
                    message: message.message || ''
                });
            }
        }
    }, [extractionStatus, isExtractingLoading, t]);

    const handleExtractionErrorUpdate = useCallback((errorMsg, wsError) => {
        const userFriendlyErrorMessage = handleWebSocketError(
            errorMsg, wsError, openModal, addNotification,
            { defaultTitle: t('dataExtractionError'), moduleName: t('serverDataExtractionModule') }
        );
        setExtractionError(userFriendlyErrorMessage);
        setExtractionStatus('error');
        setIsExtractingLoading(false);
        setExtractionProgress(null);
        showToast(userFriendlyErrorMessage || (isEn ? 'Extraction error occurred' : 'Произошла ошибка извлечения'));
    }, [openModal, addNotification, showToast, t, isEn]);

    const handleHistoryMessage = useCallback((message, wsError) => {
        setIsHistoryLoading(false);
        if (wsError || message?.type === WS_SERVER_DATA_TYPES.HISTORY_ERROR) {
            const errorText = handleWebSocketError(
                message, wsError, openModal, addNotification,
                { defaultTitle: t('errorLoadingHistory'), moduleName: t('dataExtractionHistory') }
            );
            setHistoryError(errorText);
        } else if (message?.type === WS_SERVER_DATA_TYPES.HISTORY_LIST && message.data?.history) {
            const formattedHistory = message.data.history.map(item => ({
                ...item,
                host: item.ip || item.server,
                time: new Date(item.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                format: (item.outputFormat || item.format || 'JSON').toUpperCase()
            }));
            setExtractionHistory(formattedHistory);
            setHistoryError(null);
        }
    }, [openModal, addNotification, t]);

    // --- Start extraction handler ---
    const handleExtractData = useCallback(() => {
        const activeHost = currentServerIpRef.current || targetIp || serverInfo?.hostname || serverInfo?.ip;

        if (!activeHost) {
            showToast(isEn ? 'Please specify a target server first' : 'Сначала выберите или введите целевой сервер');
            return;
        }
        if (isExtractingLoading) {
            return;
        }
        if (!isAuthenticated) {
            triggerGoogleLogin();
            return;
        }

        setIsExtractingLoading(true);
        setExtractionStatus('extracting');
        setExtractionError(null);
        setExtractedData(null);
        setExtractionProgress({
            percent: 20,
            step: t('dataExtractionInProgress'),
            message: isEn ? 'Connecting to server socket handshake...' : 'Подключение к сокету TCP handshake...'
        });
        setGeneralWsError(null);

        sendMessage(getDataAppEndpoint(WS_SERVER_DATA_TOPICS.EXTRACT), {
            ip: activeHost,
            options: extractionOptions
        });
    }, [isExtractingLoading, isAuthenticated, triggerGoogleLogin, extractionOptions, sendMessage, serverInfo, targetIp, showToast, t, isEn]);

    // Stable references
    const latestHandlersRef = useRef({});
    useEffect(() => {
        latestHandlersRef.current = {
            handleExtractionStatusUpdate,
            handleExtractionProgressUpdate,
            handleExtractionErrorUpdate,
            handleHistoryMessage,
        };
    }, [
        handleExtractionStatusUpdate,
        handleExtractionProgressUpdate,
        handleExtractionErrorUpdate,
        handleHistoryMessage,
    ]);

    // --- WebSocket Subscriptions ---
    useEffect(() => {
        if (!isWebSocketConnected || !isAuthenticated || isAuthLoading) {
            Object.values(subscriptions.current).forEach(unsub => unsub?.());
            subscriptions.current = { extractionStatus: null, extractionProgress: null, extractionError: null, history: null };
            historyRequestedRef.current = false;
            return;
        }

        setGeneralWsError(null);
        Object.values(subscriptions.current).forEach(unsub => unsub?.());

        subscriptions.current.extractionStatus = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.STATUS),
            (message, wsError) => latestHandlersRef.current.handleExtractionStatusUpdate?.(message, wsError)
        );
        subscriptions.current.extractionProgress = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.PROGRESS),
            (message, wsError) => latestHandlersRef.current.handleExtractionProgressUpdate?.(message, wsError)
        );
        subscriptions.current.extractionError = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.ERROR),
            (message, wsError) => latestHandlersRef.current.handleExtractionErrorUpdate?.(message, wsError)
        );
        subscriptions.current.history = subscribe(
            getDataUserQueueTopic(WS_SERVER_DATA_TOPICS.USER_HISTORY),
            (message, wsError) => latestHandlersRef.current.handleHistoryMessage?.(message, wsError)
        );

        const stateTimer = setTimeout(() => {
            if (isWebSocketConnected && isAuthenticated) {
                sendMessage(getDataAppEndpoint(WS_SERVER_DATA_TOPICS.GET_STATE), {});
            }
        }, 500);

        if (!historyRequestedRef.current) {
            const historyTimer = setTimeout(() => {
                if (isWebSocketConnected && isAuthenticated) {
                    setIsHistoryLoading(true);
                    setHistoryError(null);
                    sendMessage(getDataAppEndpoint(WS_SERVER_DATA_TOPICS.APP_GET_HISTORY), {});
                    historyRequestedRef.current = true;
                }
            }, 600);
            return () => { clearTimeout(stateTimer); clearTimeout(historyTimer); };
        }

        return () => {
            Object.values(subscriptions.current).forEach(unsub => unsub?.());
            subscriptions.current = { extractionStatus: null, extractionProgress: null, extractionError: null, history: null };
            clearTimeout(stateTimer);
        };
    }, [isWebSocketConnected, isAuthenticated, isAuthLoading, subscribe, sendMessage]);

    // Copy to clipboard handler
    const handleCopy = useCallback((text) => {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            showToast(isEn ? 'Data copied to clipboard' : 'Данные скопированы в буфер');
        }).catch(() => {
            showToast(isEn ? 'Failed to copy to clipboard' : 'Ошибка доступа к буферу');
        });
    }, [showToast, isEn]);

    // Download dump file handler
    const handleDownload = useCallback((content, format = 'json') => {
        if (!content) return;
        const host = (serverInfo?.hostname || serverInfo?.ip || targetIp || currentServerIpRef.current || 'server').replace(/[:.]/g, '_');
        const filename = `stormblack-${host}-dump.${format}`;
        const mimeTypes = {
            json: 'application/json',
            html: 'text/html',
            yaml: 'text/yaml',
            txt: 'text/plain'
        };
        const blob = new Blob([content], { type: mimeTypes[format] || 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast(`${isEn ? 'File saved: ' : 'Файл сохранен: '}${filename}`);
    }, [serverInfo, targetIp, showToast, isEn]);

    // History select handler
    const handleSelectHistoryItem = useCallback((item) => {
        if (!item) return;
        if (item.data) {
            setExtractedData(item.data);
            setExtractionStatus('complete');
        }
        showToast(`${isEn ? 'Loaded dump: ' : 'Загружен дамп: '}${item.host || item.server || item.ip}`);
    }, [showToast, isEn]);

    // Clear history handler
    const handleClearHistory = useCallback(() => {
        setExtractionHistory([]);
        showToast(isEn ? 'History cleared' : 'История очищена');
    }, [showToast, isEn]);

    const activeServerData = extractedData;

    return (
        <PageTransition>
            <div className="data-extraction-page-container min-h-screen text-white relative font-sans">

                <div className="w-full max-w-[1320px] mx-auto px-4 sm:px-6 relative z-10 pt-8 sm:pt-14 pb-12 sm:pb-16">

                    {/* HERO SECTION: DATA EXTRACTION */}
                    <ServerDataExtractionHeader />

                    {/* ТА ЖЕ ПЛАШКА ТЕКУЩЕГО СЕРВЕРА ИЗ MAIN / RCON */}
                    <ServerInfoCard className="w-full mx-auto relative z-40 mb-5 sm:mb-8" />

                    {/* ОСНОВНАЯ РАБОЧАЯ СЕТКА: ПАРАМЕТРЫ СЛЕВА / РЕЗУЛЬТАТЫ СПРАВА */}
                    <div className="data-main-grid">
                        
                        {/* Левая колонка: параметры и история */}
                        <div className="left-controls-column">
                            <ExtractionOptions
                                options={extractionOptions}
                                onChange={setExtractionOptions}
                                onExtract={() => {
                                    setPendingAction('extract');
                                    setIsTermsModalOpen(true);
                                }}
                                isLoading={isExtractingLoading}
                                disabled={isConnecting || isExtractingLoading}
                            />

                            <ExtractionHistory
                                history={extractionHistory}
                                isLoading={isHistoryLoading}
                                error={historyError}
                                onClearHistory={handleClearHistory}
                                onSelectHistoryItem={handleSelectHistoryItem}
                            />
                        </div>

                        {/* Правая колонка: извлечённые данные с вкладками */}
                        <ExtractionResults
                            serverData={activeServerData}
                            extractionStatus={extractionStatus}
                            isLoading={isExtractingLoading}
                            isDisabled={isConnecting}
                            progress={extractionProgress}
                            options={extractionOptions}
                            outputFormat={extractionOptions.outputFormat || 'json'}
                            onFormatChange={(newFmt) => setExtractionOptions(prev => ({ ...prev, outputFormat: newFmt }))}
                            onExtract={() => {
                                setPendingAction('extract');
                                setIsTermsModalOpen(true);
                            }}
                            onCopy={handleCopy}
                            onDownload={handleDownload}
                            targetHost={currentServerIpRef.current || targetIp || serverInfo?.hostname || serverInfo?.ip}
                        />

                    </div>

                    {/* PRO TIPS: СОВЕТЫ ПО ИЗВЛЕЧЕНИЮ ДАННЫХ */}
                    <DataExtractionTips />

                </div>

                {/* Terms Of Service Modal */}
                <TermsOfServiceModal
                    isOpen={isTermsModalOpen}
                    onClose={() => setIsTermsModalOpen(false)}
                    onAccept={() => {
                        setIsTermsModalOpen(false);
                        if (pendingAction === 'extract') {
                            handleExtractData();
                        }
                        setPendingAction(null);
                    }}
                    onDecline={() => {
                        setIsTermsModalOpen(false);
                        setPendingAction(null);
                    }}
                    t={t}
                />

            </div>
        </PageTransition>
    );
};

export default ServerDataExtractionPage;